import { AnyAction } from 'redux';
import * as navigate from 'app/area/shared/navigation';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, ComplexValue, LoadOptions } from 'app/types-shared';
import { STATE } from 'app/constants';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { isForbiddenStatus, blockAccess, isNotFoundStatus, getErrorMessage } from 'app/area/shared/actions-utils';
import {
  ClearList, RequestList, ReceiveList, FailList, ModifyItem, RequestSaveChanges, ReceiveSaveChanges,
  FailSaveChanges, RequestCodetableOptions, ReceiveCodetableOptions, FailCodetableOptions, CopyToAll, ValidationChange,
  RequestAccessCodetables, FailAccessCodetables, ReceiveAccessCodetables, RequestMetadata, ReceiveMetadata, FailMetadata,
  RequestItem, ReceiveItem, FailItem
} from './action-types';
import { blockAccessCodeMappings, caseTypeApplicationId } from './constants';
import { AccessCodetable, ModifiedCodeMappings } from 'app/area/code-mappings/types-store';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import {
  mapList, mapModifiedItems, mapPostSearch, mapPostSearchParams, getCodetableIds, mapUpdatedList
} from 'app/area/code-mappings/common/mappings';
import { api } from './api';
import { LoadCodeItemsParams } from 'app/area/code-mappings/types';
import { mapCategoryOptions, mapCurrentOptions, mapListToStore } from 'app/area/code-mappings/map-to-store';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { mapMetadataToStore } from 'app/area/code-mappings/map-metadata-to-store';
import { referenceCodetableId } from 'app/area/import-mappings/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { LoadListParams } from 'app/area/code-mappings/components/types';

export const actionCreators = {

  loadList: (id: number, params: LoadListParams): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const requestId = Date.now();
      dispatch(new RequestList(requestId));

      try {

        const missingMappings = await api.getMissingMappings(id, params);
        const codetableIds = getCodetableIds(missingMappings.missingMappings);
        const list = mapList(missingMappings);

        dispatch(new ReceiveList(
          requestId,
          params,
          codetableIds,
          list.filename,
          list.importSet,
          list.missingMappingsExists,
          list.results
        ));

      } catch (err) {
        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.codeMappings,
            translationId: blockAccessCodeMappings().id
          });
        }

        if (isNotFoundStatus(err)) {
          dispatch(
            new ErrorNotification(
              replaceTemplateString(
                getErrorMessage({
                  dispatch,
                  getState,
                  area: STATE.codeMappings,
                  translationId: blockAccessCodeMappings().no_import
                }), id)
            )
          );
          navigate.toImportView();
        }

        dispatch(new FailList(requestId));
      }
    },

  loadItem: (id: number, params: LoadListParams): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const requestId = Date.now();
      dispatch(new RequestItem(requestId, params));

      try {

        const missingMappings = await api.getMissingMappings(id, params);
        const searchParams = mapPostSearchParams(missingMappings);
        const postSearchValues = await api.getPostSearchValues(
          missingMappings?.importSet?.v,
          searchParams
        );
        const postSearch = mapPostSearch(postSearchValues);
        const updatedList = mapUpdatedList(missingMappings, postSearch);

        dispatch(new ReceiveItem(requestId, params, updatedList.results));

      } catch (err) {
        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));
        dispatch(new FailItem(requestId));
      }
    },

  modifyItem: (
    modifiedData: ModifiedCodeMappings
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ModifyItem(modifiedData));
    },

  copyToAll: (
    id: string, codetableId: number, code: any, codeCategory: ComplexValue<number>
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyToAll(id, codetableId, code, codeCategory));
    },

  saveChanges: (
    importSetId: number,
    modifiedCodeMappings: ModifiedCodeMappings[],
    validationErrorMessage: string

  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      try {
        dispatch(new RequestSaveChanges());

        const modifiedCodeMapping = modifiedCodeMappings;
        const mappedModifiedItems = mapModifiedItems(modifiedCodeMappings);

        const currentCodeMapping = modifiedCodeMapping?.[0];
        const codeId = currentCodeMapping?.code?.value;
        const codetableId = currentCodeMapping?.codetable;
        const externalValues = modifiedCodeMapping.map(mapping => mapping.id);

        isDefinedIncludingZero(codeId)
          ? await api.saveImportSetMapping(importSetId, mappedModifiedItems)
          : await api.deleteImportSetMapping(importSetId, codetableId, externalValues);

        dispatch(new ReceiveSaveChanges());
      } catch (err) {
        dispatch(new FailSaveChanges());

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new ErrorNotification(validationErrorMessage));
          dispatch(new ValidationChange(complexValidationFromServer(err.validationErrors)));
        }
      }
    },

  loadCodetableOptions: (
    id: string,
    codetableId: number,
    loadOptionsParams: LoadOptions,
    optionsParams?: LoadCodeItemsParams): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestCodetableOptions(id, loadOptionsParams));

      try {
        const parentId = loadOptionsParams && loadOptionsParams.parentId || undefined;
        const siblingId = loadOptionsParams && loadOptionsParams.siblingId || undefined;
        const code = loadOptionsParams && loadOptionsParams.code;
        const query = loadOptionsParams && loadOptionsParams.query || undefined;
        const includeDisabled = loadOptionsParams && loadOptionsParams.includeDisabled;
        const displayNotSelected = optionsParams && optionsParams.displayNotSelected;
        const ignoreCategoryFilters = codetableId === referenceCodetableId;

        const [codes, path] = await Promise.all([
          api.loadCodeItems({
            id: codetableId,
            parentId,
            siblingId,
            query,
            categoryId: loadOptionsParams && loadOptionsParams.categoryId,
            limitFieldsTo: optionsParams?.limitFieldsTo ?? ['code'],
            ...(displayNotSelected !== undefined && { displayNotSelected }),
            includeDisabled: !!includeDisabled,
            ...(code !== undefined && { code }),
            ...(codetableId === caseTypeApplicationId && query !== undefined && { returnLeavesOnly: true }),
            ...(ignoreCategoryFilters && { ignoreCategoryFilters })
          }),
          siblingId !== undefined || parentId !== undefined
            ? api.loadPath(codetableId, siblingId || parentId)
            : Promise.resolve(undefined)]);

        const wireCodes = codes && codes.results || [];
        const pathCodes = codeItemToStoreValue(path || {});

        const storeCodes = mapListToStore(wireCodes, pathCodes);
        const currentOptions = mapCurrentOptions(wireCodes);
        const codeStoreCodes = currentOptions.length > 0 ? storeCodes : [];

        dispatch(new ReceiveCodetableOptions(id, loadOptionsParams, codeStoreCodes, currentOptions));
      } catch (err) {

        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));
        dispatch(new FailCodetableOptions(id, loadOptionsParams));
      }
    },

  loadCategoryOptions: (
    id: string,
    codetableId: number,
    loadOptionsParams: LoadOptions): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestCodetableOptions(id, loadOptionsParams));

      try {
        const includeDisabled = loadOptionsParams && loadOptionsParams.includeDisabled;
        const categoryOptions = await api.loadCategoryOptions(codetableId, includeDisabled);
        const wireCategoryOptions = categoryOptions || [];
        const mappedOptions = mapCategoryOptions(wireCategoryOptions);
        const storeCodes = mapListToStore(mappedOptions, undefined);
        const currentOptions = mapCurrentOptions(mappedOptions);

        dispatch(new ReceiveCodetableOptions(id, loadOptionsParams, storeCodes, currentOptions));

      } catch (err) {

        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));
        dispatch(new FailCodetableOptions(id, loadOptionsParams));
      }
    },

  loadCodetablesAccess: (codetableId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestAccessCodetables(codetableId));

      try {
        const result = await api.loadCodetablesAccess(codetableId);
        const codetablesAccess = (result && result.length > 0) ? result.find(f => f.id === codetableId) : {} as AccessCodetable;

        dispatch(new ReceiveAccessCodetables(codetableId, codetablesAccess));
      } catch (err) {
        dispatch(new FailAccessCodetables(codetableId));
      }
    },


  loadMetadata: (id: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestMetadata(id));

      try {
        const metadataWire = await api.loadMetadata(id);
        const metadata = mapMetadataToStore(metadataWire);

        dispatch(new ReceiveMetadata(id, metadata));

      } catch (err) {
        dispatch(new FailMetadata(id));
      }
    },

  clearList: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      const requestId = Date.now();
      dispatch(new ClearList(requestId));
    },

  displayError: (errorMessage: string): ActionDispatching<any, any> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
